// JSComponents/ProductCard.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faTimes, faLink } from '@fortawesome/free-solid-svg-icons';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { collectDisplaySizes, isSizeInStock } from '../utils/sortSizes';
import { openExternalLink } from '../utils/openExternalLink';
import { getSessionId, getDevice, trackProductView, trackInterest, getAnalyticsUserId, trackMetaViewContent } from '../utils/analytics';
import '../StylesComponents/product-card.css';

// Impression batch: accumulate and flush every 3 s to avoid per-card requests
const impressionQueue = [];
let impressionFlushTimer = null;
const flushImpressions = () => {
  if (impressionQueue.length === 0) return;
  const batch = impressionQueue.splice(0, impressionQueue.length);
  import('../utils/api').then(({ default: apiModule }) => {
    apiModule.post('/api/products/impression', { impressions: batch }).catch(() => {});
  });
};
const queueImpression = (imp) => {
  impressionQueue.push(imp);
  if (!impressionFlushTimer) {
    impressionFlushTimer = setTimeout(() => {
      impressionFlushTimer = null;
      flushImpressions();
    }, 3000);
  }
};

function ProductCard({ product, currentUser, className = '', theme = 'light', onSizeSelectorToggle, feedSection = 'unknown', position = null, showRetailer = false }) {
  const { wishlistItems, isInWishlist, toggleWishlist, removeFromWishlist, loading } = useWishlist();
  const isWishlisted = isInWishlist(product._id || product.productId || product.id);
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const cardRef = useRef(null);
  const impressionTracked = useRef(false);
  const { convertPrice, getCurrencySymbol } = useCurrency();

  // Track impression via IntersectionObserver (fires once per mount when ≥50% visible)
  useEffect(() => {
    if (!cardRef.current || impressionTracked.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !impressionTracked.current) {
          impressionTracked.current = true;
          observer.disconnect();
          queueImpression({
            productId: product._id || product.id,
            feedSection,
            position,
            sessionId: getSessionId(),
            device: getDevice(),
            userId: getAnalyticsUserId(),
          });
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product._id]);

  // Close selector on click outside
  useEffect(() => {
    if (!showSizeSelector) return;

    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setShowSizeSelector(false);
        if (onSizeSelectorToggle) onSizeSelectorToggle(false);
      }
    };

    // Use capture phase to ensure it runs before other handlers if needed, 
    // or just standard to allow interaction.
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSizeSelector, onSizeSelectorToggle]);

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    
    // If already wishlisted, find the item and remove it
    if (isWishlisted) {
      const pId = String(product._id || product.productId || product.id).toLowerCase();
      const existingItem = wishlistItems.find(item => 
        String(item.productId || '').toLowerCase() === pId || 
        String(item._id || '').toLowerCase() === pId
      );
      
      if (existingItem) {
        removeFromWishlist(existingItem._id);
      } else {
        // Fallback
        toggleWishlist(product, 'M');
      }
      return;
    }

    // Partnered brands do not display size selectors and are wishlisted without a garment size
    if (product.isPartnered) {
      setIsHeartAnimating(true);
      setTimeout(() => setIsHeartAnimating(false), 300);
      toggleWishlist(product, 'ONESIZE');
      return;
    }

    // Toggle selector for adding standard/non-partnered items
    const newState = !showSizeSelector;
    setShowSizeSelector(newState);
    if (onSizeSelectorToggle) onSizeSelectorToggle(newState);
  };

  const confirmSizeSelection = async (size) => {
    setIsHeartAnimating(true);
    setTimeout(() => setIsHeartAnimating(false), 300);
    
    setShowSizeSelector(false);
    if (onSizeSelectorToggle) onSizeSelectorToggle(false);
    
    await toggleWishlist(product, size);
  };

  const handleCartIconClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Track click before redirect
    try {
      await api.post('/api/products/click', {
        productId: product._id || product.id,
        feedSection,
        position,
        sessionId: getSessionId(),
        device: getDevice(),
        userId: getAnalyticsUserId(),
        referrer: document.referrer || 'direct',
      });
    } catch (err) {
      console.error('Click tracking failed');
    }

    if (product.affiliateUrl) {
      openExternalLink(product.affiliateUrl);
    }
  };

  const calculateDiscount = () => {
    if (!product) return 0;
    const orig = typeof product.originalPrice === 'string' ? parseFloat(product.originalPrice.replace('$', '')) : (product.originalPrice || 0);
    const curr = typeof product.price === 'string' ? parseFloat(product.price.replace('$', '')) : (product.price || 0);
    if (orig > curr && orig > 0) {
      return Math.round(((orig - curr) / orig) * 100);
    }
    return 0;
  };

  const discount = product.discount || calculateDiscount();
  // We keep discount calculated but not currently rendered in this minimal card design

  const stripPartnerSizeSuffix = (name) => {
    const raw = String(name || '').replace(/\s+/g, ' ').trim();
    if (!raw) return '';
    let result = raw;
    result = result.replace(/(?:\s*(?:-|\/|,|\(|\[)?\s*(?:size\s*)?(?:XXS|XS|S|M|L|XL|XXL|2XL|3XL|4XL|5XL|OS|ONE\s*SIZE(?:\s*FITS\s*ALL)?|\d{1,2}(?:\.\d+)?)\s*[\)\]]?\s*)$/i, '').trim();
    return result.replace(/\s{2,}/g, ' ').replace(/[\s\-_/|,:]+$/, '').trim() || raw;
  };

  const formatPriceLocal = (price) => {
    if (price === undefined || price === null) return '0.00';
    let numericPrice = price;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
    }
    if (isNaN(numericPrice)) return '0.00';
    const converted = convertPrice(numericPrice);
    return `${getCurrencySymbol()}${converted}`;
  };

  const cleanText = (text) => {
    if (!text) return '';
    const nameWithoutSku = text
      .replace(/\b[A-Z0-9]{10,}\b/g, '')
      .replace(/_/g, ' ')
      .trim();
    const nameWithoutSize = product.isPartnered ? stripPartnerSizeSuffix(nameWithoutSku) : nameWithoutSku;
    return nameWithoutSize.toUpperCase();
  };

  if (!product) return null;

  const trackCardTap = () => {
    trackInterest(product, 3);
    trackProductView({
      productId: product._id || product.id,
      eventType: 'card_tap',
      feedSection,
      position,
    });
    trackMetaViewContent(product);
  };

  const renderSizeSelector = () => {
    const displaySizes = collectDisplaySizes([product]);
    
    const closeSelector = (e) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      setShowSizeSelector(false);
      if (onSizeSelectorToggle) onSizeSelectorToggle(false);
    };

    // Never invent a size when the retailer has not supplied verified size data.
    if (displaySizes.length === 0) {
      return (
        <div className="card-size-selector-overlay" onClick={closeSelector}>
          <div className="size-selector-header" onClick={(e) => e.stopPropagation()}>
            <span>NO_SIZES_FOUND</span>
            <button className="close-selector-btn" onClick={closeSelector}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="size-grid-container empty" onClick={(e) => e.stopPropagation()}>
            <span className="size-selector-empty-message">NO VERIFIED RETAILER SIZES</span>
          </div>
        </div>
      );
    }

    return (
      <div className="card-size-selector-overlay" onClick={closeSelector}>
        <div className="size-selector-header" onClick={(e) => e.stopPropagation()}>
          <span>SELECT_SIZE</span>
          <button className="close-selector-btn" onClick={closeSelector}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="size-grid-container dynamic-grid" onClick={(e) => e.stopPropagation()}>
          {displaySizes.slice(0, 16).map((size) => {
            const inStock = isSizeInStock(product, size);
            return (
              <button
                key={size}
                className={`size-grid-btn ${!inStock ? 'sold-out' : ''}`}
                disabled={!inStock}
                title={!inStock ? 'Sold Out' : ''}
                onClick={() => inStock && confirmSizeSelection(size)}
              >
                {String(size).toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div 
      className={`product-card-minimal ${theme === 'dark' ? 'inverted' : ''} ${className}`} 
      ref={cardRef}
    >
      <div className="product-card-visual">
        {showSizeSelector && renderSizeSelector()}
        {product.otherOffersCount > 0 && !product.isPartnered && (
          <div className="multiple-offers-badge">
            COMPARE {product.otherOffersCount + 1} OFFERS
          </div>
        )}
        <Link to={`/product/${product._id || product.id}`} onClick={trackCardTap}>
          <img 
            src={product.image || (product.images && product.images[0]) || '/images/default-product.jpg'} 
            alt={product.name || 'Product'} 
            className={`product-card-img ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
        </Link>
        
        <div className="product-card-overlay-actions">
          <button 
            className={`overlay-action-btn ${isWishlisted ? 'active' : ''} ${isHeartAnimating ? 'animating' : ''}`}
            onClick={handleWishlistClick}
            disabled={loading}
            style={{ color: isWishlisted ? '#ff4444' : 'inherit' }}
          >
            <FontAwesomeIcon icon={faHeart} />
          </button>
          <button 
            className="overlay-action-btn"
            onClick={handleCartIconClick}
            title="GET DEAL"
          >
            <FontAwesomeIcon icon={faLink} />
          </button>
        </div>
      </div>

      <div className="product-card-content">
        <Link to={`/all-brands?brand=${encodeURIComponent(product.brand || '')}`} style={{ textDecoration: 'none' }}>
          <div className="product-card-brand">
            {cleanText(product.brand || 'Stockroom Partner')}
          </div>
        </Link>
        {showRetailer && (product.retailer || product.merchantName) && (
          <div className="product-card-retailer">
            {(product.retailer || product.merchantName)}
          </div>
        )}
        <div className="product-info-group">
          <Link to={`/product/${product._id || product.id}`} className="product-name-link" onClick={trackCardTap}>
            <h3 className="product-card-title">
              {cleanText(product.name || 'UNNAMED_PRODUCT')}
            </h3>
          </Link>
          <div className="product-card-price-row">
            <span className="price-main">{formatPriceLocal(product.price)}</span>
            <div className="price-right-wrap">
              {product.originalPrice > product.price && (
                <>
                  <span className="price-original">{formatPriceLocal(product.originalPrice)}</span>
                  <span className="price-discount-tag">{discount}% OFF</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
